import React, { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import secureAxios from "../utils/secureAxios";
import { formatPhone } from "../utils/formatPhone";
import SignatureField from "../components/SignatureField";
import { toast } from "react-toastify";

const Profile = () => {
  const { user: authUser, refreshUser, logout } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone_number: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_id: "",
    postal_code: "",
    status_id: "",
    signature: "",
    signature_updated_at: null,
  });

  const [states, setStates] = useState([]);
  const [passwordData, setPasswordData] = useState({ current: "", new: "" });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const signatureRef = useRef(null);

  // Helper function to format date/time
  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `Today at ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else {
      return date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  // Fetch current user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use the authenticated user from context
        if (!authUser || !authUser.user_id) {
          setError("Please log in to view your profile.");
          setLoading(false);
          return;
        }

        let profileData = null;
        let profileFetched = false;

        // Try multiple endpoints to get the profile data
        const endpoints = [
          { url: "/api/users/profile/current", name: "current user profile" },
          { url: "/api/users/profile", name: "generic profile" },
          { url: `/api/users/${authUser.user_id}`, name: "user by ID" },
        ];

        for (const endpoint of endpoints) {
          try {
            const response = await secureAxios.get(endpoint.url);
            profileData = response.data;
            profileFetched = true;
            break;
          } catch (err) {
            continue;
          }
        }

        if (!profileFetched || !profileData) {
          throw new Error("Unable to fetch profile from any endpoint");
        }

        // Set form data with the fetched profile
        setFormData({
          name: profileData.name || "",
          email: profileData.email || "",
          phone_number: formatPhone(profileData.phone_number || ""),
          address_line_1: profileData.address?.address_line_1 || "",
          address_line_2: profileData.address?.address_line_2 || "",
          city: profileData.address?.city || "",
          state_id: profileData.address?.state_id || "",
          postal_code: profileData.address?.postal_code || "",
          status_id: profileData.status_id || "",
          signature: profileData.signature || "",
          signature_updated_at: profileData.signature_updated_at || null,
        });

        if (profileData.signature_updated_at) {
          setLastSaved(profileData.signature_updated_at);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError(
            "Profile not found. This might be a permissions issue. Please contact support."
          );
        } else if (err.response?.status === 403) {
          setError("You don't have permission to view this profile.");
        } else {
          setError(
            err.response?.data?.error ||
              err.message ||
              "Error loading profile. Please try again."
          );
        }
      } finally {
        setLoading(false);
      }
    };

    if (authUser) {
      fetchUserProfile();
    }
  }, [authUser]);

  // Fetch states
  useEffect(() => {
    secureAxios
      .get("/api/states")
      .then((res) => setStates(res.data))
      .catch((err) => {});
  }, []);

  // Clear message after timeout
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(""), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone_number") {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      const formatted = formatPhone(digits);
      setFormData((prev) => ({ ...prev, phone_number: formatted }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSignatureChange = useCallback((signatureData) => {
    // Update form data immediately
    setFormData((prev) => {
      const newData = { ...prev, signature: signatureData || "" };
      return newData;
    });
  }, []);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    if (!authUser?.user_id) {
      setMessage("❌ Session error. Please refresh the page.");
      toast.error("Session error");
      return;
    }

    // Initialize with current form data
    let signatureToSubmit = formData.signature;

    // AUTO-CAPTURE SIGNATURE BEFORE SUBMISSION
    if (signatureRef.current) {
      // Get the pad reference
      const pad = signatureRef.current.getPad();

      if (pad) {
        // Check if pad has content (even if not officially "ended")
        const isEmpty = pad.isEmpty();

        if (!isEmpty) {
          // Get the signature data directly
          const capturedSignature = pad.toDataURL();

          // Use the captured signature for submission
          signatureToSubmit = capturedSignature;

          // Also update the form state for UI consistency
          setFormData((prev) => ({ ...prev, signature: capturedSignature }));
        }
      }
    }

    // Validate signature size for HIPAA compliance
    if (signatureToSubmit && signatureToSubmit.length > 100000) {
      setMessage("❌ Signature is too large. Please try a simpler signature.");
      toast.error("Signature is too large");
      return;
    }

    setIsSaving(true);

    try {
      // Build payload - use the captured signature
      const payload = {
        name: formData.name,
        email: formData.email,
        phone_number: formData.phone_number.replace(/\D/g, ""),
        address_line_1: formData.address_line_1,
        address_line_2: formData.address_line_2,
        city: formData.city,
        state_id: formData.state_id,
        postal_code: formData.postal_code,
        status_id: formData.status_id,
        tenant_id: authUser.tenant_id,
        signature: signatureToSubmit || "", // Use the captured signature
      };

      const response = await secureAxios.put(
        `/api/users/profile/${authUser.user_id}`,
        payload
      );

      setMessage("✅ Profile updated successfully!");
      toast.success("Profile updated successfully!");

      // Update last saved time
      const now = new Date().toISOString();
      setLastSaved(now);
      setFormData((prev) => ({ ...prev, signature_updated_at: now }));

      // Refresh user data in auth context
      await refreshUser();

      // Verify signature was saved
      const verifyResponse = await secureAxios.get(
        `/api/users/profile/current`
      );

      // Update local state with verified data
      if (verifyResponse.data) {
        setFormData((prev) => ({
          ...prev,
          signature: verifyResponse.data.signature || "",
          signature_updated_at: verifyResponse.data.signature_updated_at,
        }));
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || "Failed to update profile.";
      setMessage(`❌ ${errorMessage}`);
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    if (!authUser?.user_id) {
      setMessage("❌ Session error. Please refresh the page.");
      return;
    }

    if (!passwordData.current || !passwordData.new) {
      setMessage("❌ Please fill in both password fields.");
      toast.error("Please fill in both password fields");
      return;
    }

    if (passwordData.new.length < 8) {
      setMessage("❌ New password must be at least 8 characters.");
      toast.error("New password must be at least 8 characters");
      return;
    }

    setIsSaving(true);

    try {
      const response = await secureAxios.put("/api/auth/update-password", {
        user_id: authUser.user_id,
        current_password: passwordData.current,
        new_password: passwordData.new,
      });

      if (response.data.requiresLogin) {
        setMessage(
          "✅ Password updated! For your security, you'll be logged out."
        );
        toast.success("Password updated! You'll be logged out for security.");
        setPasswordData({ current: "", new: "" });

        // Use AuthContext logout instead of manual clearing
        setTimeout(() => {
          logout();
        }, 2000);
      } else {
        setMessage("✅ Password updated!");
        toast.success("Password updated!");
        setPasswordData({ current: "", new: "" });
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setMessage("❌ Current password is incorrect.");
        toast.error("Current password is incorrect");
      } else {
        setMessage("❌ Failed to update password.");
        toast.error("Failed to update password");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearSignature = () => {
    signatureRef.current?.clear();
    setFormData((prev) => ({
      ...prev,
      signature: "",
      signature_updated_at: null,
    }));
  };

  // Show loading state
  if (loading) {
    return (
      <div className="container mt-4">
        <h2>My Profile</h2>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="container mt-4">
        <h2>My Profile</h2>
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </div>
        {error.includes("Session expired") && (
          <div className="text-center">
            <p>Redirecting to login page...</p>
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-lg-10 col-xl-8">
          <h2 className="mb-4">My Profile</h2>

          {/* User Info Header */}
          <div className="mb-4 p-3 bg-light rounded">
            <div className="row">
              <div className="col-sm-6">
                <p className="mb-1">
                  <strong>Logged in as:</strong> {authUser?.email}
                </p>
                <p className="mb-0">
                  <strong>Role:</strong> {authUser?.role}
                </p>
              </div>
              <div className="col-sm-6 text-sm-end">
                <p className="mb-1">
                  <strong>Tenant:</strong> {authUser?.tenant_name || "N/A"}
                </p>
                <p className="mb-0">
                  <strong>User ID:</strong>{" "}
                  <small className="text-muted">{authUser?.user_id}</small>
                </p>
              </div>
            </div>
          </div>

          {message && (
            <div
              className={`alert ${
                message.includes("✅") ? "alert-success" : "alert-danger"
              } alert-dismissible fade show`}
            >
              {message}
              <button
                type="button"
                className="btn-close"
                onClick={() => setMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}

          {/* Profile Update Form */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">Profile Information</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleProfileUpdate} className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Name</label>
                  <input
                    name="name"
                    value={formData.name}
                    className="form-control"
                    onChange={handleChange}
                    disabled
                    title="Please contact an administrator to change your name"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    className="form-control"
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Phone Number</label>
                  <input
                    name="phone_number"
                    type="tel"
                    value={formData.phone_number}
                    className="form-control"
                    onChange={handleChange}
                    placeholder="123-456-7890"
                  />
                </div>

                <div className="col-12">
                  <hr className="my-3" />
                  <h6 className="text-muted mb-3">Address Information</h6>
                </div>

                <div className="col-12">
                  <label className="form-label">Address Line 1</label>
                  <input
                    name="address_line_1"
                    value={formData.address_line_1}
                    className="form-control"
                    onChange={handleChange}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Address Line 2</label>
                  <input
                    name="address_line_2"
                    value={formData.address_line_2}
                    className="form-control"
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-5">
                  <label className="form-label">City</label>
                  <input
                    name="city"
                    value={formData.city}
                    className="form-control"
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">State</label>
                  <select
                    name="state_id"
                    value={formData.state_id}
                    className="form-select"
                    onChange={handleChange}
                  >
                    <option value="">Select State</option>
                    {states.map((s) => (
                      <option key={s.state_id} value={s.state_id}>
                        {s.state_code} - {s.state_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Postal Code</label>
                  <input
                    name="postal_code"
                    value={formData.postal_code}
                    className="form-control"
                    onChange={handleChange}
                    pattern="[0-9]{5}"
                    maxLength="5"
                  />
                </div>

                <div className="col-12">
                  <hr className="my-3" />
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="text-muted mb-0">
                      Digital Signature
                      {formData.signature && (
                        <span className="badge bg-success ms-2">
                          <i className="bi bi-check-circle me-1"></i>
                          On File
                        </span>
                      )}
                    </h6>
                    {formData.signature && formData.signature_updated_at && (
                      <small className="text-muted">
                        <i className="bi bi-clock-history me-1"></i>
                        Last signed:{" "}
                        {formatDateTime(formData.signature_updated_at)}
                      </small>
                    )}
                  </div>
                </div>

                {/* Signature Field */}
                <div className="col-12">
                  <SignatureField
                    ref={signatureRef}
                    value={formData.signature}
                    onChange={handleSignatureChange}
                    label="Your Signature"
                    required={false}
                    disabled={false}
                    penColor="blue"
                    backgroundColor="white"
                    maxSize={100000}
                    className="mb-3"
                  />

                  {/* Show current signature if exists */}
                  {formData.signature && formData.signature.length > 0 && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="w-100">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Current Signature on File</h6>
                            <button
                              type="button"
                              onClick={handleClearSignature}
                              className="btn btn-sm btn-outline-danger"
                              title="Remove signature"
                            >
                              <i className="bi bi-trash me-1"></i>
                              Remove
                            </button>
                          </div>
                          <div className="bg-white p-3 border rounded">
                            <img
                              src={formData.signature}
                              alt="Your signature"
                              style={{
                                maxWidth: "100%",
                                height: "auto",
                                maxHeight: "150px",
                                display: "block",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          </div>
                          {(formData.signature_updated_at || lastSaved) && (
                            <div className="mt-2 text-center">
                              <small className="text-muted">
                                <i className="bi bi-clock-history me-1"></i>
                                Signed:{" "}
                                {formatDateTime(
                                  formData.signature_updated_at || lastSaved
                                )}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        Update Profile
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Password Update Form */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Change Password</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handlePasswordUpdate}>
                <input
                  type="hidden"
                  name="username"
                  value={authUser?.email || formData.email}
                  autoComplete="username"
                />
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      Current Password <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      name="current-password"
                      value={passwordData.current}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          current: e.target.value,
                        })
                      }
                      required
                      autoComplete="current-password"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      New Password <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      name="new-password"
                      value={passwordData.new}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          new: e.target.value,
                        })
                      }
                      required
                      minLength="8"
                      autoComplete="new-password"
                    />
                    <small className="text-muted">
                      Minimum 8 characters. Use a mix of letters, numbers, and
                      symbols.
                    </small>
                  </div>
                </div>
                <div className="mt-3">
                  <button
                    type="submit"
                    className="btn btn-warning"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-key me-2"></i>
                        Update Password
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Security Notice */}
          <div className="alert alert-info mt-4">
            <i className="bi bi-shield-check me-2"></i>
            <strong>Security Notice:</strong> Your profile information is
            protected and all changes are logged for HIPAA compliance. Your
            digital signature is encrypted and stored securely.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
